import { useEffect, useState } from 'react';
import { PageBody } from '../../elements/page-body';
import { useTranslation } from 'react-i18next';
import { useHttpClient } from '../../shared/http-client';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in react-leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41], // Half of the icon width and full height
    popupAnchor: [1, -34],
});

L.Marker.prototype.options.icon = DefaultIcon;

const statusColors: Record<string, string> = {
    CRITICAL: "bg-red-100 text-red-700",
    OK: "bg-green-100 text-green-700",
};

const getEcoScoreColor = (category: string): string => {
    const colors: Record<string, string> = {
        EXCELLENT: "text-green-400",
        GOOD: "text-green-600",
        NOT_GOOD: "text-yellow-500",
        BAD: "text-orange-500",
        VERY_BAD: "text-red-600",
        UNKNOWN: "text-gray-500",
    };
    return colors[category] || "text-gray-500";
};

// Component to auto-fit map bounds
const FitBounds = ({ coordinates }: { coordinates: any[] }) => {
    const map = useMap();

    useEffect(() => {
        if (coordinates && coordinates.length > 1) {
            const bounds = L.latLngBounds(
                coordinates.map((c: any) => [c.latitude, c.longitude])
            );
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [coordinates, map]);

    return null;
};


export const TripDetail = () => {

    const { t } = useTranslation() as { t: (key: string) => string };
    const navigate = useNavigate();
    const { id } = useParams();
    const { api, state } = useHttpClient();
    const [data, setData] = useState<any | null>(null);

    useEffect(() => {
        if (id) {
            api(`/fleet/trips/${id}/`, 'GET');
        }
    }, [id]);

    useEffect(() => {
        if (state.data) {
            if (state.path.includes('trips')) {
                setData(state.data);
            }
        }

        if (state.error) {
            //toast.error('Server Error');
        }
    }, [state]);

    if (!data) {
        return null;
    }

    const { vehicle, driver, tracker_device, coordinates } = data;

    const lastCoord = coordinates?.[coordinates.length - 1];
    const mapCenter = lastCoord
        ? [lastCoord.latitude, lastCoord.longitude]
        : [41.3874, 2.1686]; // fallback: Barcelona


    return (
        <>
            <div
                className="pt-8 pl-8 cursor-pointer"
                onClick={() => navigate(-1)}
            >
                <span className='text-black pt-[5px]'>{'< '}</span>
                <span className='text-black text-[12px]'>{t('common.button.back')}</span>
            </div>

            <div className='w-full'>
                <div className="max-w-6xl mx-auto p-6 space-y-2">
                    {/* Vehicle Header */}
                    <div className='flex w-full'>
                        <div className="bg-white rounded-xl shadow p-6 flex gap-6 w-2/3">
                            <img
                                src={vehicle.photo_display_url || "/car-placeholder.png"}
                                alt="vehicle"
                                className="w-auto h-36 object-cover rounded-lg"
                            />
                            <div>
                                <h2 className="text-3xl font-bold">
                                    {vehicle.brand_name} {vehicle.model?.name}
                                </h2>
                                <p className="text-gray-600 text-lg">🚘 {t('common.form-label.matricula')}: {vehicle.plate}</p>

                                {/* <div className="flex gap-3 mt-2">
                                    {["insurance_status", "inspection_status"].map(
                                        (key) => (
                                            <span
                                                key={key}
                                                className={`text-xs px-2 py-1 rounded-lg font-semibold ${statusColors[vehicle[key]]
                                                    }`}
                                            >
                                                {key.replace("_status", "")}: {vehicle[key]}
                                            </span>
                                        )
                                    )}
                                </div> */}
                            </div>
                        </div>
                        <div className="bg-white shadow p-4 rounded-xl  w-1/3 ml-4 flex justify-between">

                            <div>
                                <h3 className="font-bold text-lg">👨‍✈️ {t('common.label.conductor')}</h3>
                                <p>{driver.full_name}</p>
                                <p className="text-sm text-gray-500">{driver.email}</p>
                            </div>
                            <div className='pl-4'>
                                <img
                                    src={driver.photo_display_url || "/car-placeholder.png"}
                                    alt="vehicle"
                                    className="w-auto h-36 object-cover rounded-lg"
                                />
                            </div>

                        </div>
                    </div>


                    {/* GRID Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Driver */}


                        {/* Tracker Device */}
                        <div className="bg-white shadow p-4 rounded-xl">
                            <h3 className="font-bold text-lg">📡 Tracker Device</h3>
                            <p><strong>IMEI:</strong> {tracker_device.IMEI}</p>
                            {/*    <p><strong>Tel:</strong> {tracker_device.phone_number}</p> */}
                        </div>

                        <div className="bg-white shadow p-4 rounded-xl">
                            

                            <div className='flex'><h3 className="text-[14px] font-semibold mb-1">Eco Score</h3>
                                <p className={`font-bold pl-2 text-[13px] ${getEcoScoreColor(data.eco_score_category)}`}>{data.eco_score_category}</p>
                            </div>
                            <div className='flex'><h3 className="text-[14px] font-semibold mb-1">{t('common.label.distancia-recorrida')}</h3>
                                <p className="pl-2  text-[13px]">{(data.odometer/1000).toFixed(2)} km</p>
                            </div>
                            <div className='flex'><h3 className="text-[14px] font-semibold mb-1">{t('common.label.distancia-recorrida-esperada')}</h3>
                                <p className="pl-2  text-[13px]">{(data.expected_distance/1000).toFixed(2)} km</p>
                            </div>
                            <div className='flex'><h3 className="text-[14px] font-semibold mb-1">{t('common.label.duracion')}</h3>
                                <p className='pl-2 text-[13px]'>{data.duration_display}</p></div>
                            <div className='flex'><h3 className="text-[14px] font-semibold mb-1">{t('common.label.duracion-esperada')}</h3>
                                <p className="pl-2  text-[13px]">{data.expected_duration_display}</p>
                            </div>
                        </div>
                        {/* Fuel Info */}
                        <div className="bg-white shadow p-4 rounded-xl">
                            <h3 className="font-bold text-lg">⛽ {t('common.label.combustible')}</h3>
                            <p><strong>{t('common.label.inicio')}:</strong> {data.start_percentage_fuel_level}%</p>
                            <p><strong>{t('common.form-label.final')}:</strong> {data.end_percentage_fuel_level}%</p>
                            <p><strong>{t('common.label.usado')}:</strong> {data.fuel_used} L</p>
                        </div>


                    </div>

                    <div className="bg-white rounded-xl shadow p-4 grid grid-cols-2 gap-6">

                        <div>
                            <h3 className="text-lg font-semibold mb-2">📍 {t('common.form-label.salida')}</h3>
                            <p>{data.start_address}</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-2">🏁 {t('common.form-label.llegada')}</h3>
                            <p>{data.end_address}</p>
                        </div>






                    </div>

                    {/* Coordinates + Map */}
                    {/*   <div className="bg-white rounded-xl shadow p-4">
                            <h3 className="font-bold text-lg mb-3">🧭 Ubicación reciente</h3>
                            {lastCoord ? (
                                <MapContainer
                                    center={mapCenter as any}
                                    zoom={14}
                                    className="h-64 rounded-lg"
                                >
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <Marker position={mapCenter as any}>
                                        <Popup>
                                            Última posición <br />
                                            {new Date(lastCoord.timestamp).toLocaleString()}
                                        </Popup>
                                    </Marker>
                                </MapContainer>
                            ) : (
                                <p className="text-gray-500 text-sm">
                                    No hay coordenadas disponibles
                                </p>
                            )}
                        </div> */}
                    {/* Coordinates + Route Map */}
                    <div className="bg-white rounded-xl shadow p-4">
                        <h3 className="font-bold text-lg mb-3">🧭 {t('common.label.ruta')}</h3>

                        {coordinates && coordinates.length > 1 ? (
                            <MapContainer
                                center={[coordinates[0].latitude, coordinates[0].longitude] as any}
                                zoom={15}
                                className="h-64 rounded-lg"
                            >
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                <FitBounds coordinates={coordinates} />

                                {/* Polyline of full route */}
                                <Polyline
                                    positions={coordinates.map((c: any) => [
                                        c.latitude,
                                        c.longitude,
                                    ])}
                                />

                                {/* Start marker */}
                                <Marker
                                    position={[coordinates[0].latitude, coordinates[0].longitude] as any}
                                >
                                    <Popup>📍 {t('common.label.inicio')}</Popup>
                                </Marker>

                                {/* End marker */}
                                <Marker
                                    position={[
                                        coordinates[coordinates.length - 1].latitude,
                                        coordinates[coordinates.length - 1].longitude,
                                    ] as any}
                                >
                                    <Popup>🏁 {t('common.label.final')}</Popup>
                                </Marker>
                            </MapContainer>
                        ) : (
                            <p className="text-gray-500 text-sm">
                                No data
                            </p>
                        )}
                    </div>


                </div>

            </div>

        </>

    );
};